import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { AlertTriangle, History, Boxes } from "lucide-react";

export default function Inventory() {
  const [alerts, setAlerts] = useState({ threshold: 5, items: [] });
  const [products, setProducts] = useState([]);
  const [history, setHistory] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ product_id: "", change: "", reason: "" });

  const load = useCallback(async () => {
    const [a, p, h] = await Promise.all([
      api.get("/store/inventory/alerts"),
      api.get("/store/products"),
      api.get("/store/inventory/history"),
    ]);
    setAlerts(a.data);
    setProducts(p.data);
    setHistory(h.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.product_id || form.change === "") { toast.error("Select a product and quantity change"); return; }
    try {
      await api.post("/store/inventory/adjust", { product_id: form.product_id, change: parseInt(form.change), reason: form.reason });
      toast.success("Inventory adjusted");
      setOpen(false);
      setForm({ product_id: "", change: "", reason: "" });
      load();
    } catch { toast.error("Adjustment failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Inventory</h1>
          <p className="text-muted-foreground mt-1">Adjust stock and review history.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-lg" data-testid="adjust-inventory-btn"><Boxes className="h-4 w-4 mr-1.5" /> Adjust stock</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Adjust inventory</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Product</Label>
                <Select value={form.product_id} onValueChange={(v) => setForm((f) => ({ ...f, product_id: v }))}>
                  <SelectTrigger data-testid="adjust-product-select"><SelectValue placeholder="Select product" /></SelectTrigger>
                  <SelectContent>
                    {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.quantity} in stock)</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Change (use negative to remove)</Label>
                <Input type="number" value={form.change} onChange={(e) => setForm((f) => ({ ...f, change: e.target.value }))}
                  placeholder="e.g. 10 or -5" data-testid="adjust-change-input" />
              </div>
              <div className="space-y-2">
                <Label>Reason</Label>
                <Input value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                  placeholder="Restock, damage, correction..." data-testid="adjust-reason-input" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={submit} data-testid="adjust-submit-btn">Apply adjustment</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="rounded-xl border-border">
        <div className="p-5 border-b border-border flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <h3 className="font-semibold">Low stock alerts</h3>
          <span className="text-sm text-muted-foreground">(≤ {alerts.threshold})</span>
        </div>
        {alerts.items.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground text-center">Everything is well stocked.</p>
        ) : (
          <div className="divide-y divide-border">
            {alerts.items.map((p) => (
              <div key={p.id} className="flex items-center justify-between px-5 py-3">
                <span className="text-sm font-medium">{p.name}</span>
                <span className={`text-sm font-semibold ${p.quantity === 0 ? "text-destructive" : "text-amber-600"}`}>
                  {p.quantity === 0 ? "Out of stock" : `${p.quantity} left`}
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="rounded-xl border-border overflow-hidden">
        <div className="p-5 border-b border-border flex items-center gap-2">
          <History className="h-4 w-4 text-muted-foreground" />
          <h3 className="font-semibold">Adjustment history</h3>
        </div>
        {history.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground text-center">No adjustments yet.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead className="text-right">Change</TableHead>
                <TableHead className="text-right">Result</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.map((h) => (
                <TableRow key={h.id}>
                  <TableCell className="font-medium">{h.product_name}</TableCell>
                  <TableCell className={`text-right font-medium ${h.change >= 0 ? "text-emerald-600" : "text-destructive"}`}>
                    {h.change >= 0 ? "+" : ""}{h.change}
                  </TableCell>
                  <TableCell className="text-right">{h.resulting_qty}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{h.reason || "—"}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{new Date(h.created_at).toLocaleDateString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
